import React from 'react';
import { kebabCase } from 'lodash';
import { useField, useFormikContext } from 'formik';
import { Select } from 'syngenta-digital-cropwise-react-ui-kit';
import ChevronDown from 'assets/chevron_down.svg?react';
import { FieldLabel, FieldMessage } from './FieldChrome';
import useFieldMessage from './useFieldMessage';
import './FormikSelect.css';
import { FieldItem, FieldKey } from './types';

type FormikSelectProps = {
  name: string;
  items: FieldItem[];
  id?: string;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
  errorMessage?: string | null;
  onSelect?: (item: FieldItem) => void;
};

const isEmptyKey = (key: FieldKey | undefined) =>
  key === null || key === undefined;

const FormikSelect = ({
  name,
  items,
  id,
  label,
  placeholder,
  disabled = false,
  getPopupContainer,
  errorMessage,
  onSelect
}: FormikSelectProps) => {
  const [field] = useField(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const { hasError, message } = useFieldMessage(name, errorMessage);
  const selectId = id ?? kebabCase(name);

  // grow-ui lists offered a `{ key: null }` row to reset the field. The kit
  // shows the placeholder for an empty value, so that row becomes the clear
  // button instead of an option.
  const choices = items.filter(item => !isEmptyKey(item.key));
  const canClear = items.some(item => isEmptyKey(item.key));
  // Options are keyed by position because antd only accepts string or number
  // values, and several fields store booleans.
  const options = choices.map((item, index) => ({
    value: index,
    label: item.value
  }));
  const selectedIndex = choices.findIndex(item => item.key === field.value);

  const handleChange = (index: number | undefined) => {
    const nextKey = index === undefined ? null : choices[index].key;
    setFieldTouched(name, true);
    setFieldValue(name, nextKey);
    if (onSelect) {
      onSelect(
        items.find(item => item.key === nextKey) ?? { key: null, value: '' }
      );
    }
  };

  return (
    <div className="h-8 cwf-kit-select-field">
      {label && <FieldLabel htmlFor={selectId}>{label}</FieldLabel>}
      <Select
        id={selectId}
        className="w-full cwf-kit-select"
        value={selectedIndex === -1 ? undefined : selectedIndex}
        placeholder={placeholder}
        disabled={disabled}
        getPopupContainer={getPopupContainer}
        allowClear={canClear}
        options={options}
        onChange={handleChange}
        status={hasError ? 'error' : undefined}
        suffixIcon={<ChevronDown width={18} height={18} aria-hidden="true" />}
      />
      <FieldMessage message={message} />
    </div>
  );
};

export default FormikSelect;
